import reSpecializedData from "../homebrew/reSpecialized.json"
import mcmDroidsData from "../homebrew/mcmDroids.json"
import specializationsData from "../data/specializations.json"
import talentsData from "../data/talents.json"
import careersData from "../data/careers.json"
import speciesData from "../data/species.json"
import forcePowersData from "../data/forcePowers.json"
import forceAbilitiesData from "../data/forceAbilities.json"
import signatureAbilitiesData from "../data/signatureAbilities.json"
import signatureAbilityNodesData from "../data/signatureAbilityNodes.json"

const PACKS = [
    {
        id: 'mcmDroids',
        speciesReplacements: mcmDroidsData.species,
        speciesKeys: mcmDroidsData.replaces.species,
    },
    {
        id: 'reSpecialized',
        specializationReplacements: reSpecializedData.specializations,
        specializationKeys: reSpecializedData.replaces.specializations,
        specializationRemoves: reSpecializedData.removes?.specializations,
        talentAdditions: reSpecializedData.talents,
        careerAdditions: reSpecializedData.careerAdditions,
        careerRemovals: reSpecializedData.careerRemovals,
        newCareers: reSpecializedData.newCareers,
        forcePowerReplacements: reSpecializedData.forcePowers,
        forcePowerKeys: reSpecializedData.replaces?.forcePowers,
        forceAbilityAdditions: reSpecializedData.forceAbilities,
        sigAbilityReplacements: reSpecializedData.signatureAbilities,
        sigAbilityRemoves: reSpecializedData.removes?.signatureAbilities,
        sigAbilityNodeAdditions: reSpecializedData.signatureAbilityNodes,
    }
];

const getArr = (files, key, fallback) =>
    files[key] ? JSON.parse(files[key]) : [...fallback];

let _cacheKey = null;
let _effectiveFiles = null;
const _parsedByKey = {};

// Called by App.js after its initial applyHomebrew call so the first getEffective
// from any utility is a cache hit rather than a blocking recomputation.
export function primeEffectiveCache(effectiveFiles) {
    if (effectiveFiles === _effectiveFiles) return;
    _cacheKey = localStorage.getItem('homebrewState') || '{}';
    _effectiveFiles = effectiveFiles;
    for (const k in _parsedByKey) delete _parsedByKey[k];
    for (const key of Object.keys(effectiveFiles)) {
        if (effectiveFiles[key]) {
            try { _parsedByKey[key] = JSON.parse(effectiveFiles[key]); } catch (e) { /* skip non-JSON */ }
        }
    }
}

export function getEffective(key, fallback) {
    const homebrewStr = localStorage.getItem('homebrewState') || '{}';
    if (homebrewStr !== _cacheKey) {
        _cacheKey = homebrewStr;
        const stored = JSON.parse(localStorage.getItem('appFiles') || '{}');
        _effectiveFiles = applyHomebrew(stored, JSON.parse(homebrewStr));
        for (const k in _parsedByKey) delete _parsedByKey[k];
    }
    if (!(key in _parsedByKey)) {
        _parsedByKey[key] = _effectiveFiles[key] ? JSON.parse(_effectiveFiles[key]) : [...fallback];
    }
    return _parsedByKey[key];
}

export function applyHomebrew(storedFiles, homebrewState) {
    if (!homebrewState || PACKS.every(p => !homebrewState[p.id])) {
        return storedFiles;
    }

    const files = { ...storedFiles };

    for (const pack of PACKS) {
        if (!homebrewState[pack.id]) continue;

        if (pack.speciesKeys?.length) {
            const cur = getArr(files, 'species', speciesData);
            files.species = JSON.stringify(
                cur.map(s => pack.speciesReplacements.find(r => r.Key === s.Key) ?? s)
            );
        }

        if (pack.specializationKeys?.length || pack.specializationReplacements?.length) {
            const cur = getArr(files, 'specializations', specializationsData);
            const replacedKeys = new Set(pack.specializationKeys || []);
            const curKeys = new Set(cur.map(s => s.Key));
            const newSpecs = cur.map(s => pack.specializationReplacements.find(r => r.Key === s.Key) ?? s);
            pack.specializationReplacements
                .filter(r => !replacedKeys.has(r.Key) && !curKeys.has(r.Key))
                .forEach(r => newSpecs.push(r));
            files.specializations = JSON.stringify(newSpecs);
        }

        if (pack.specializationRemoves?.length) {
            const cur = getArr(files, 'specializations', specializationsData);
            const drop = new Set(pack.specializationRemoves);
            files.specializations = JSON.stringify(cur.filter(s => !drop.has(s.Key)));
        }

        if (pack.talentAdditions?.length) {
            const cur = getArr(files, 'talents', talentsData);
            const existingKeys = new Set(cur.map(t => t.Key));
            const toAdd = pack.talentAdditions.filter(t => !existingKeys.has(t.Key));
            if (toAdd.length) files.talents = JSON.stringify([...cur, ...toAdd]);
        }

        const adds = pack.careerAdditions || {};
        const removes = pack.careerRemovals || {};
        const touched = new Set([...Object.keys(adds), ...Object.keys(removes)]);
        if (touched.size > 0) {
            const cur = getArr(files, 'careers', careersData);
            files.careers = JSON.stringify(cur.map(career => {
                if (!touched.has(career.Key)) return career;
                const drop = new Set(removes[career.Key] || []);
                const gain = adds[career.Key] || [];
                const specs = (career.Specializations || []).filter(k => !drop.has(k));
                const have = new Set(specs);
                gain.forEach(k => { if (!have.has(k)) specs.push(k); });
                return { ...career, Specializations: specs };
            }));
        }

        if (pack.newCareers?.length) {
            const cur = getArr(files, 'careers', careersData);
            const have = new Set(cur.map(c => c.Key));
            const toAdd = pack.newCareers.filter(c => !have.has(c.Key));
            if (toAdd.length) files.careers = JSON.stringify([...cur, ...toAdd]);
        }

        if (pack.forcePowerReplacements?.length) {
            const cur = getArr(files, 'forcePowers', forcePowersData);
            const replaceMap = Object.fromEntries(pack.forcePowerReplacements.map(p => [p.Key, p]));
            const replacedKeys = new Set(pack.forcePowerKeys || []);
            const replaced = cur.map(p => replaceMap[p.Key] ?? p);
            pack.forcePowerReplacements
                .filter(p => !replacedKeys.has(p.Key) && !cur.some(c => c.Key === p.Key))
                .forEach(p => replaced.push(p));
            files.forcePowers = JSON.stringify(replaced);
        }

        if (pack.forceAbilityAdditions?.length) {
            const cur = getArr(files, 'forceAbilities', forceAbilitiesData);
            const existingKeys = new Set(cur.map(a => a.Key));
            const toAdd = pack.forceAbilityAdditions.filter(a => !existingKeys.has(a.Key));
            if (toAdd.length) files.forceAbilities = JSON.stringify([...cur, ...toAdd]);
        }

        if (pack.sigAbilityReplacements?.length || pack.sigAbilityRemoves?.length) {
            const cur = getArr(files, 'signatureAbilities', signatureAbilitiesData);
            const drop = new Set(pack.sigAbilityRemoves || []);
            const replaceMap = Object.fromEntries((pack.sigAbilityReplacements || []).map(s => [s.Key, s]));
            const next = cur.filter(s => !drop.has(s.Key)).map(s => replaceMap[s.Key] ?? s);
            const have = new Set(next.map(s => s.Key));
            (pack.sigAbilityReplacements || []).filter(s => !have.has(s.Key)).forEach(s => next.push(s));
            files.signatureAbilities = JSON.stringify(next);
        }

        if (pack.sigAbilityNodeAdditions?.length) {
            const cur = getArr(files, 'signatureAbilityNodes', signatureAbilityNodesData);
            const existingKeys = new Set(cur.map(n => n.Key));
            const toAdd = pack.sigAbilityNodeAdditions.filter(n => !existingKeys.has(n.Key));
            if (toAdd.length) files.signatureAbilityNodes = JSON.stringify([...cur, ...toAdd]);
        }
    }

    return files;
}
